import { useCallback, useEffect } from 'react';

import { lockScroll, unlockScroll } from 'src/general/helpers/scrollLock';

type HookProps = {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  closeOnLinkClick?: boolean;
  closeOnBackdropClick?: boolean;
  onCloseModal?: () => void;
};

type HookReturn = {
  openDialog: () => void;
  closeDialog: () => void;
};

/**
 * @description Handles the opening and closing of a dialog
 * @param dialogRef - The reference to the dialog element
 * @param closeOnLinkClick - Whether to close the dialog when a link is clicked
 * @param onCloseModal - The function to call when the dialog is closed
 * @returns An object with the openDialog and closeDialog functions
 */
export const useDialog = ({
  dialogRef,
  closeOnLinkClick = true,
  closeOnBackdropClick = true,
  onCloseModal,
}: HookProps): HookReturn => {
  const openDialog = useCallback(() => {
    dialogRef.current?.showModal();
  }, [dialogRef]);

  const closeDialog = useCallback(() => {
    dialogRef.current?.close();
  }, [dialogRef]);

  // Handle opening and closing the dialog
  useEffect(() => {
    let handleClosePopover: ((e: ToggleEvent) => void) | undefined;
    let handleKeyDown: ((e: KeyboardEvent) => void) | undefined;

    if (dialogRef.current) {
      handleClosePopover = (e) => {
        if (e.newState === 'closed') {
          unlockScroll();
          onCloseModal?.();
        } else if (e.newState === 'open') {
          lockScroll();
        }
      };
      handleKeyDown = (e) => {
        if (e.key === 'Escape' && dialogRef.current) {
          closeDialog();
        }
      };
      dialogRef.current.addEventListener('toggle', handleClosePopover as EventListener);
      dialogRef.current.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (dialogRef.current && !!handleClosePopover) {
        dialogRef.current.removeEventListener('toggle', handleClosePopover as EventListener);
      }
      if (dialogRef.current && !!handleKeyDown) {
        dialogRef.current.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [onCloseModal]);

  // Handle click on anchor tags to close the popover
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only proceed if the event target is a link or a descendant of a link
      const target = event.target as HTMLElement;
      const isLink = target.closest('a');
      const isNavigationButton = target.closest('button[data-type="navigation"]');
      if (!isLink && !isNavigationButton) {
        return;
      }
      if (onCloseModal) {
        setTimeout(() => {
          onCloseModal();
        }, 50);
      }
    };

    if (dialogRef.current && closeOnLinkClick) {
      dialogRef.current.addEventListener('click', handleClick);
    }

    return () => {
      if (dialogRef.current && closeOnLinkClick) {
        dialogRef.current.removeEventListener('click', handleClick);
      }
    };
  }, [onCloseModal, closeOnLinkClick]);

  // Add logic to close dialog when clicking backdrop (i.e., when clicking outside dialog content)
  useEffect(() => {
    const handleBackdropClick = (event: MouseEvent) => {
      const dialogEl = dialogRef.current;
      if (!dialogEl) return;

      // Only react if click target IS the dialog itself (not a child), i.e., the backdrop area
      if (event.target === dialogEl) {
        closeDialog();
      }
    };

    if (dialogRef.current && closeOnBackdropClick) {
      dialogRef.current.addEventListener('click', handleBackdropClick);
    }

    return () => {
      if (dialogRef.current && closeOnBackdropClick) {
        dialogRef.current.removeEventListener('click', handleBackdropClick);
      }
    };
  }, [dialogRef, closeDialog, closeOnBackdropClick]);

  return { openDialog, closeDialog };
};
